import { useEffect } from "react";
import { useQuery, type ApolloError } from "@apollo/client";
import { CNC_DELETION_OPERATION_STATUS } from "../../../CustomerProvisioningApi";

const IN_PROGRESS = "in_progress";
const POLL_INTERVAL_MS = 30000;

export interface DeletionOperationEvent {
  component?: string;
  step?: string;
  target?: string;
  status?: string;
  dry_run?: boolean;
  item_count?: number | null;
  reference_id?: string;
  message?: string;
  inserted_at?: string;
}

interface DeletionOperationStatus {
  op_id: string;
  overall_status: string;
  events: DeletionOperationEvent[];
}

interface DeletionOperationStatusData {
  cnc_deletion_operation_status?: DeletionOperationStatus;
}

interface UseDeletionOperationStatusParams {
  accountUuid: string;
  opId: string | null;
}

interface UseDeletionOperationStatusResult {
  overallStatus?: string;
  events: DeletionOperationEvent[];
  loading: boolean;
  error?: ApolloError;
}

/**
 * Polls the CNC deletion-operation status by op_id while the operation is
 * still in progress. A real op can sit `in_progress` for several minutes
 * (Databricks cold start), so polling continues until the backend reports a
 * terminal `overall_status` (`succeeded` / `failed`). While `opId` is falsy the
 * query is skipped.
 */
const useDeletionOperationStatus = ({
  accountUuid,
  opId,
}: UseDeletionOperationStatusParams): UseDeletionOperationStatusResult => {
  const { data, loading, error, startPolling, stopPolling } =
    useQuery<DeletionOperationStatusData>(CNC_DELETION_OPERATION_STATUS, {
      variables: { account_uuid: accountUuid, op_id: opId },
      skip: !opId,
      fetchPolicy: "network-only",
    });

  const status = data?.cnc_deletion_operation_status;
  const overallStatus = status?.overall_status;

  useEffect(() => {
    if (!opId) {
      return undefined;
    }

    if (error) {
      // Don't keep polling silently against a failing query — stop and let the
      // caller surface the error.
      stopPolling();
    } else if (overallStatus === IN_PROGRESS) {
      startPolling(POLL_INTERVAL_MS);
    } else if (overallStatus) {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [opId, overallStatus, error, startPolling, stopPolling]);

  return {
    overallStatus,
    events: status?.events ?? [],
    loading,
    error,
  };
};

export default useDeletionOperationStatus;
